import { humanizeError } from "@/lib/errors";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Boxes, Braces, Building2, List as ListIcon, MessagesSquare, MoreHorizontal, Pencil, Plug, RefreshCw, Rocket, RotateCw, Sparkles, SquareTerminal, Terminal, Trash2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AgentAvatar, AppearanceDialog } from "@/components/AgentAvatar";

import {
  APIError,
  deleteProject,
  deploy,
  listDeployments,
  restartAgent,
  updateStaleProjects,
  type Deployment,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { CopyButton, ErrorState, PageHeader, Spinner } from "@/components/Page";
import { cn } from "@/lib/utils";

// Office Mode pulls in three.js — lazy-load it so the default List view never
// downloads the 3D bundle.
const OfficeMode = lazy(() => import("@/components/office/OfficeMode"));

type AgentsView = "list" | "office";
const VIEW_KEY = "cb-agents-view";
function initialView(): AgentsView {
  try {
    return localStorage.getItem(VIEW_KEY) === "office" ? "office" : "list";
  } catch {
    return "list";
  }
}

function errMsg(e: unknown, fallback: string) {
  return humanizeError(e, fallback);
}

function subdomainOf(d: Deployment): string {
  if (d.subdomain) return d.subdomain;
  try {
    return new URL(d.url).hostname.split(".")[0] ?? "";
  } catch {
    return "";
  }
}

export function Projects() {
  const [deps, setDeps] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [updatingAll, setUpdatingAll] = useState(false);
  const [connecting, setConnecting] = useState<Deployment | null>(null);
  const [deleting, setDeleting] = useState<Deployment | null>(null);
  const [editingAppearance, setEditingAppearance] = useState<Deployment | null>(null);
  const [view, setView] = useState<AgentsView>(initialView);
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const changeView = (v: AgentsView) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* ignore */
    }
  };

  const cancelledRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr(null);
    return listDeployments()
      .then(setDeps)
      .catch((e) => setLoadErr(errMsg(e, "Couldn’t load agents")))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () => () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const pollUntilFresh = useCallback(() => {
    let ticks = 0;
    const tick = async () => {
      if (cancelledRef.current) return;
      ticks += 1;
      try {
        const next = await listDeployments();
        if (cancelledRef.current) return;
        setDeps(next);
        if (!next.some((d) => d.stale)) {
          setUpdatingAll(false);
          toast.success("All agents updated.");
          return;
        }
      } catch {
        // transient list failure: keep polling until the cap
      }
      if (cancelledRef.current) return;
      if (ticks >= 45) {
        setUpdatingAll(false);
        toast.error("Some agents are still updating — refresh to check.");
        return;
      }
      timerRef.current = setTimeout(() => void tick(), 4000);
    };
    timerRef.current = setTimeout(() => void tick(), 4000);
  }, [toast]);

  const onUpdate = useCallback(
    async (d: Deployment) => {
      const subdomain = subdomainOf(d);
      const label = d.name ?? d.subdomain ?? d.berth_project_id;
      if (!subdomain) {
        toast.error("Can't update: this agent has no subdomain.");
        return;
      }
      const ok = await confirm({
        title: `Update ${label}?`,
        description:
          "This redeploys with the latest tools of this agent’s selected integrations. The URL and access tokens stay the same; the running sandbox is replaced.",
        confirmText: "Update",
      });
      if (!ok) return;
      setUpdating(d.deployment_id);
      try {
        await deploy(d.name ?? d.berth_project_id, subdomain, d.berth_project_id);
        toast.success("Agent updated");
        void load();
      } catch (e) {
        toast.error(errMsg(e, "Update failed"));
      } finally {
        setUpdating(null);
      }
    },
    [confirm, load, toast],
  );

  const onRestart = useCallback(
    async (d: Deployment) => {
      const label = d.name ?? d.subdomain ?? d.berth_project_id;
      const ok = await confirm({
        title: `Restart ${label}?`,
        description:
          "Reboots this agent's sandbox onto the latest runtime. Its URL, access tokens, selected integrations, playbook, and stored data are preserved; if a tool update is pending, it's applied at the same time. In-flight requests will briefly fail while it restarts.",
        confirmText: "Restart",
      });
      if (!ok) return;
      setUpdating(d.deployment_id);
      try {
        await restartAgent(d.berth_project_id);
        toast.success("Agent restarted");
        void load();
      } catch (e) {
        if (e instanceof APIError && e.message === "update_in_progress") {
          toast.error("An update is already in progress — try again in a moment.");
        } else {
          toast.error(errMsg(e, "Restart failed"));
        }
      } finally {
        setUpdating(null);
      }
    },
    [confirm, load, toast],
  );

  const anyStale = deps.some((d) => d.stale);
  const staleCount = deps.filter((d) => d.stale).length;

  const onUpdateAll = useCallback(async () => {
    const ok = await confirm({
      title: "Update all stale agents?",
      description: `Redeploys ${staleCount} agent(s) with the current tools of all your connected integrations. Each keeps its URL and access tokens; the running sandbox is replaced. This runs in the background and can take a little while.`,
      confirmText: "Update all",
    });
    if (!ok) return;
    setUpdatingAll(true);
    try {
      const { started } = await updateStaleProjects();
      if (started === 0) {
        toast.success("Everything is already up to date.");
        void load();
        setUpdatingAll(false);
      } else {
        toast.success(`Updating ${started} agent(s)…`);
        pollUntilFresh();
      }
    } catch (e) {
      if (e instanceof APIError && e.message === "update_in_progress") {
        toast.error("An update is already in progress.");
      } else {
        toast.error(errMsg(e, "Couldn't start update"));
      }
      setUpdatingAll(false);
    }
  }, [confirm, load, pollUntilFresh, staleCount, toast]);

  return (
    <div>
      <PageHeader
        title="Agents"
        description="Each agent is an isolated sandbox with its own /mcp endpoint, playbook, and access tokens."
        action={
          <div className="flex items-center gap-2">
            <ViewToggle view={view} onChange={changeView} />
            {staleCount > 0 && (
              <Button variant="outline" onClick={() => void onUpdateAll()} disabled={updatingAll}>
                {updatingAll ? <Spinner /> : <RefreshCw className="h-4 w-4" />}
                Update all ({staleCount})
              </Button>
            )}
            <Button onClick={() => navigate("/agents/new")}>
              <Rocket /> New agent
            </Button>
          </div>
        }
      />

      {anyStale && (
        <div className="mb-4 rounded-md border border-warning/40 bg-warning/10 px-4 py-2 text-sm text-warning">
          A server's tools changed since these projects were deployed.
        </div>
      )}

      {loadErr ? (
        <ErrorState message={loadErr} onRetry={load} />
      ) : loading ? (
        view === "office" ? (
          <OfficeLoading />
        ) : (
          <div className="divide-y overflow-hidden rounded-xl border">
            {Array.from({ length: 5 }).map((_, i) => (
              <AgentRowSkeleton key={i} />
            ))}
          </div>
        )
      ) : deps.length === 0 ? (
        <EmptyAgents onCreate={() => navigate("/agents/new")} />
      ) : view === "office" ? (
        <Suspense fallback={<OfficeLoading />}>
          <OfficeMode agents={deps} onOpen={(id) => navigate(`/agents/${id}`)} />
        </Suspense>
      ) : (
        <div className="divide-y overflow-hidden rounded-xl border">
          {deps.map((d) => (
            <AgentRow
              key={d.deployment_id}
              d={d}
              busy={updating === d.deployment_id}
              onConnect={() => setConnecting(d)}
              onUpdate={() => void onUpdate(d)}
              onRestart={() => void onRestart(d)}
              onDelete={() => setDeleting(d)}
              onEditAppearance={() => setEditingAppearance(d)}
            />
          ))}
        </div>
      )}

      <ConnectDialog deployment={connecting} onClose={() => setConnecting(null)} />
      {editingAppearance && (
        <AppearanceDialog
          open
          onOpenChange={(v) => !v && setEditingAppearance(null)}
          projectId={editingAppearance.berth_project_id}
          name={editingAppearance.name ?? editingAppearance.subdomain ?? editingAppearance.berth_project_id}
          emoji={editingAppearance.emoji}
          color={editingAppearance.color}
          onSaved={() => {
            setEditingAppearance(null);
            load();
          }}
        />
      )}

      <DeleteProjectDialog
        deployment={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          toast.success("Agent deleted");
          void load();
        }}
      />
    </div>
  );
}

// ViewToggle flips between the default List and the 3D Office. The choice is
// persisted (see changeView), so it's fully revertible.
function ViewToggle({ view, onChange }: { view: AgentsView; onChange: (v: AgentsView) => void }) {
  return (
    <div className="inline-flex items-center rounded-lg border p-0.5">
      {(
        [
          { id: "list", label: "List", icon: ListIcon },
          { id: "office", label: "Office", icon: Building2 },
        ] as const
      ).map((o) => {
        const Icon = o.icon;
        const on = view === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5" /> {o.label}
          </button>
        );
      })}
    </div>
  );
}

function OfficeLoading() {
  return (
    <div className="flex h-[72vh] min-h-[480px] w-full items-center justify-center rounded-xl border bg-[#0a0b12] text-sm text-white/70">
      <span className="flex items-center gap-2">
        <Spinner /> Entering the office…
      </span>
    </div>
  );
}

// StatusPill is the agent's live state, shown as a small dot + label.
function StatusPill({ d }: { d: Deployment }) {
  const live = !!d.url;
  const tone = !live ? "text-muted-foreground" : d.stale ? "text-warning" : "text-success";
  const dot = !live ? "bg-muted-foreground" : d.stale ? "bg-warning" : "bg-success";
  const label = !live ? "Not deployed" : d.stale ? "Update available" : "Live";
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", tone)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      {label}
    </span>
  );
}

// AgentRow is one agent in the list — reads like a roster of coworkers. The whole
// row opens the chat-first workspace; the actions menu sits above the click overlay.
function AgentRow({
  d,
  busy,
  onConnect,
  onUpdate,
  onRestart,
  onDelete,
  onEditAppearance,
}: {
  d: Deployment;
  busy: boolean;
  onConnect: () => void;
  onUpdate: () => void;
  onRestart: () => void;
  onDelete: () => void;
  onEditAppearance: () => void;
}) {
  const name = d.name ?? d.subdomain ?? d.berth_project_id;
  const sub = subdomainOf(d);
  return (
    <div className="group relative flex items-center gap-3 bg-card px-4 py-3 transition-colors hover:bg-accent/40">
      <Link to={`/agents/${d.berth_project_id}`} className="absolute inset-0" aria-label={`Open ${name}`} />
      {/* z-10 lifts the avatar above the full-row Link overlay so a click edits
          the appearance instead of opening the agent (same trick as the ⋯ menu). */}
      <button
        type="button"
        onClick={onEditAppearance}
        className="group/av relative z-10 shrink-0 rounded-full transition-transform hover:scale-105"
        title="Customize avatar"
        aria-label={`Customize ${name}`}
      >
        <AgentAvatar name={name} emoji={d.emoji} color={d.color} />
        <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-background bg-muted text-muted-foreground opacity-0 transition-opacity group-hover/av:opacity-100">
          <Pencil className="h-2 w-2" />
        </span>
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{name}</span>
          <StatusPill d={d} />
        </div>
        {sub && <div className="truncate font-mono text-xs text-muted-foreground">{sub}</div>}
      </div>
      <span className="hidden text-xs text-muted-foreground md:block">{fmtDate(d.created_at)}</span>
      <span className="hidden items-center gap-1 text-sm font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100 sm:inline-flex">
        <MessagesSquare className="h-4 w-4" /> Open chat
      </span>
      <div className="relative z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Agent actions" disabled={busy}>
              {busy ? <Spinner /> : <MoreHorizontal className="h-4 w-4" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onConnect}>
              <Plug /> Connect
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onUpdate} disabled={!d.stale}>
              <RefreshCw /> Update tools
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onRestart}>
              <RotateCw /> Restart agent
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive">
              <Trash2 /> Delete agent
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function AgentRowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 w-40 animate-pulse rounded bg-muted" />
        <div className="h-3 w-24 animate-pulse rounded bg-muted" />
      </div>
      <div className="h-3 w-16 animate-pulse rounded bg-muted" />
    </div>
  );
}

function EmptyAgents({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed py-20 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Sparkles className="h-7 w-7" />
      </div>
      <div className="text-base font-medium">No agents yet</div>
      <p className="max-w-sm text-sm text-muted-foreground">
        Create your first agent to get a hosted <span className="font-mono">/mcp</span> endpoint, a playbook, and a chat
        you can talk to.
      </p>
      <Button onClick={onCreate} className="mt-1">
        <Rocket className="h-4 w-4" /> New agent
      </Button>
    </div>
  );
}

// DeleteProjectDialog gates an irreversible project delete behind a
// type-to-confirm: the user must type the project's exact name. Deleting a
// project tears down its VM, volumes, custom domains, and access tokens.
function DeleteProjectDialog({
  deployment,
  onClose,
  onDeleted,
}: {
  deployment: Deployment | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const name = deployment ? (deployment.name ?? deployment.subdomain ?? deployment.berth_project_id) : "";

  // Reset the typed confirmation whenever the dialog opens for a project.
  useEffect(() => {
    setConfirmText("");
  }, [deployment]);

  const armed = confirmText.trim() === name && name !== "";

  const submit = async () => {
    if (!deployment || !armed) return;
    setBusy(true);
    try {
      await deleteProject(deployment.berth_project_id);
      onDeleted();
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={deployment !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-destructive">Delete {name}?</DialogTitle>
          <DialogDescription>
            This permanently destroys the project's sandbox, persistent volumes, custom domains, and access tokens.
            Clients using its <span className="font-mono">/mcp</span> URL will stop working. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="del-confirm">
            Type <span className="font-mono font-medium text-foreground">{name}</span> to confirm
          </Label>
          <Input
            id="del-confirm"
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && armed && void submit()}
            placeholder={name}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void submit()} disabled={!armed || busy}>
            {busy ? <Spinner /> : "Delete agent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ConnectDialog shows copy-paste recipes for pointing an MCP client at a
// project's /mcp endpoint. The bearer token is a placeholder, never the real
// secret: the deployments list masks it (full token shown only at deploy
// time), so we tell the user to substitute the one they saved.
const TOKEN_PLACEHOLDER = "YOUR_BEARER_TOKEN";

type ConnectTab = { id: string; label: string; icon: LucideIcon; lang: string; code: string };

function ConnectDialog({ deployment, onClose }: { deployment: Deployment | null; onClose: () => void }) {
  const [active, setActive] = useState("curl");
  const endpoint = deployment?.url ? `${deployment.url}/mcp` : "";
  const projectName = deployment?.name ?? deployment?.subdomain ?? deployment?.berth_project_id ?? "codebridge";

  const tabs: ConnectTab[] = [
    {
      id: "curl",
      label: "cURL",
      icon: Terminal,
      lang: "bash",
      code: [
        `curl -X POST ${endpoint} \\`,
        `  -H "Authorization: Bearer ${TOKEN_PLACEHOLDER}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -H "Accept: application/json, text/event-stream" \\`,
        `  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`,
      ].join("\n"),
    },
    {
      id: "claude",
      label: "Claude Code",
      icon: SquareTerminal,
      lang: "bash",
      code: `claude mcp add --transport http ${projectName} ${endpoint} \\\n  --header "Authorization: Bearer ${TOKEN_PLACEHOLDER}"`,
    },
    {
      id: "json",
      label: "Cursor / Desktop",
      icon: Braces,
      lang: "mcp.json",
      code: JSON.stringify(
        { mcpServers: { [projectName]: { url: endpoint, headers: { Authorization: `Bearer ${TOKEN_PLACEHOLDER}` } } } },
        null,
        2,
      ),
    },
    {
      id: "generic",
      label: "Any client",
      icon: Boxes,
      lang: "config",
      code: [
        `Endpoint   ${endpoint}`,
        `Transport  Streamable HTTP`,
        `Auth       Authorization: Bearer ${TOKEN_PLACEHOLDER}`,
      ].join("\n"),
    },
  ];
  const current = tabs.find((t) => t.id === active) ?? tabs[0];

  return (
    <Dialog open={deployment !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plug className="h-4 w-4 text-primary" /> Connect to {projectName}
          </DialogTitle>
          <DialogDescription>Point any MCP client at this agent's endpoint.</DialogDescription>
        </DialogHeader>

        {/* Endpoint strip — the one fact every client needs, always visible. */}
        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Endpoint
          </span>
          <code className="min-w-0 flex-1 break-all font-mono text-xs">{endpoint}</code>
          <CopyButton value={endpoint} />
        </div>

        {/* Client tabs — one focused recipe at a time instead of a wall of blocks. */}
        <div className="-mx-1 flex flex-wrap gap-1.5 px-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            const on = t.id === active;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActive(t.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  on
                    ? "border-primary/40 bg-primary/10 text-foreground"
                    : "border-transparent bg-muted/40 text-muted-foreground hover:bg-muted/70 hover:text-foreground",
                )}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Terminal card — wraps long lines, so NEVER a horizontal scrollbar. */}
        <div className="overflow-hidden rounded-lg border bg-[hsl(240_10%_8%)] shadow-sm">
          <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
              <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-white/40">{current.lang}</span>
            </div>
            <div className="text-white">
              <CopyButton value={current.code} />
            </div>
          </div>
          <pre className="whitespace-pre-wrap break-words p-4 font-mono text-xs leading-relaxed text-[#e4e4e7] [overflow-wrap:anywhere]">
            {current.code}
          </pre>
        </div>

        <p className="text-xs text-muted-foreground">
          Replace <code className="rounded bg-muted px-1 py-0.5 font-mono">{TOKEN_PLACEHOLDER}</code> with the bearer
          token shown when you created this project.
        </p>
      </DialogContent>
    </Dialog>
  );
}

function fmtDate(s: string) {
  return s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
}
